import { useEffect, useState } from 'react';

interface HeroSectionProps {
  onOpenBooking: () => void;
  onOpenDealer: () => void;
}

export default function HeroSection({ onOpenBooking, onOpenDealer }: HeroSectionProps) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <section id="hero" className="relative min-h-screen bg-[#050608] overflow-hidden flex flex-col">

      {/* Full-screen background image - shared mobile & desktop */}
      <div className="absolute inset-0">
        <img
          src="https://static.readdy.ai/image/1153be8c8a2f65c98102af48ca4fb482/a3094956868b8a969f49f3932e8a47c4.jpeg"
          alt="フィジオショット"
          className="w-full h-full object-cover object-[60%_center] md:object-center scale-[1.0] md:scale-[1.2]"
        />
        {/* Base dark overlay - lighter on mobile so image shows through */}
        <div className="absolute inset-0 bg-[#050608]/45 md:bg-[#050608]/35" />
        {/* Gradient - mobile: bottom-heavy so text at bottom is readable; desktop: left-to-right */}
        <div className="absolute inset-0 bg-gradient-to-t from-[#050608]/90 via-[#050608]/40 to-[#050608]/10 md:bg-none" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#050608]/75 via-[#050C20]/30 to-transparent hidden md:block" />
        {/* Bottom fade */}
        <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-t from-[#050608] to-transparent" />
        {/* Blue shimmer */}
        <div className="absolute top-0 left-0 w-[600px] h-[600px] rounded-full bg-[#1E6BFF]/12 blur-[120px]" />
        {/* Grid overlay */}
        <div className="absolute inset-0 opacity-[0.04]" style={{backgroundImage: 'linear-gradient(rgba(90,154,255,0.8) 1px, transparent 1px), linear-gradient(90deg, rgba(90,154,255,0.8) 1px, transparent 1px)', backgroundSize: '60px 60px'}} />
      </div>

      {/* Content - positioned at bottom on mobile, centered on desktop */}
      <div className="relative z-10 flex-1 w-full max-w-7xl mx-auto px-4 md:px-6 flex flex-col justify-end md:justify-center pb-10 md:pb-0 pt-20 min-h-screen">
        <div
          className={`max-w-3xl transition-all duration-1000 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="flex items-center gap-3 mb-4 md:mb-6">
            <span className="h-0.5 w-8 md:w-10 bg-[#1E6BFF]" />
            <span className="text-[#5A9AFF] text-xs font-bold tracking-[0.2em] md:tracking-[0.25em] uppercase">
              Doctor Supervised · Korean Patent
            </span>
          </div>

          <h1 className="text-3xl md:text-5xl lg:text-7xl font-black text-white leading-[1.08] mb-4 md:mb-6 tracking-tight">
            フィジオショットという<br />
            <span className="bg-gradient-to-r from-[#5A9AFF] to-[#1E6BFF] bg-clip-text text-transparent">
              新たな提案
            </span>
          </h1>

          <p className="text-white text-sm md:text-lg leading-relaxed mb-6 md:mb-3 max-w-xl">
            痛みが消えない。回復しない。<br />
            その原因は・・・・・
          </p>

          <div className="flex flex-col sm:flex-row gap-3 mb-6 md:mb-12">
            <button
              onClick={onOpenBooking}
              className="group px-7 py-3.5 md:py-4 bg-gradient-to-b from-[#2A7FFF] to-[#0D4FCC] hover:from-[#3D8FFF] hover:to-[#1A5FDD] text-white font-bold text-sm md:text-base rounded-lg transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 shadow-[0_0_25px_rgba(30,107,255,0.6)] border-t border-[#5A9AFF]/30"
            >
              <span>施術を予約する</span>
              <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform" />
            </button>
            <button
              onClick={onOpenDealer}
              className="px-7 py-3.5 md:py-4 border border-white/30 hover:border-[#1E6BFF] text-white hover:text-[#5A9AFF] font-bold text-sm md:text-base rounded-lg transition-all cursor-pointer whitespace-nowrap backdrop-blur-sm"
            >
              導入相談をする
            </button>
          </div>

          <div className="flex flex-wrap gap-3 md:gap-6 pt-4 md:pt-6 border-t border-white/15 md:border-white/20">
            {[
              { icon: 'ri-award-line', text: '医師監修' },
              { icon: 'ri-patent-line', text: '韓国特許取得' },
              { icon: 'ri-run-line', text: 'プロアスリート使用' },
              { icon: 'ri-building-2-line', text: '全国34施設導入' },
            ].map((badge) => (
              <div key={badge.text} className="flex items-center gap-1.5 md:gap-2">
                <div className="w-4 h-4 md:w-5 md:h-5 flex items-center justify-center">
                  <i className={`${badge.icon} text-[#1E6BFF] text-xs md:text-sm`} />
                </div>
                <span className="text-white text-xs md:text-sm whitespace-nowrap">{badge.text}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Floating badges - desktop only */}
        <div
          className={`hidden md:block transition-all duration-1000 delay-300 ${
            visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'
          }`}
        >
          <div className="absolute bottom-28 right-16 bg-[#0D1020]/80 border border-[#1A2545] rounded-xl px-4 py-3 backdrop-blur-md shadow-[0_0_20px_rgba(30,107,255,0.2)]">
            <p className="text-[#1E6BFF] text-xs font-black tracking-widest uppercase">Korean Patent</p>
            <p className="text-[#E8EFFF] text-sm font-bold">韓国特許取得済み</p>
          </div>
          <div className="absolute top-1/3 right-16 bg-gradient-to-b from-[#2A7FFF] to-[#0D4FCC] text-white rounded-xl px-4 py-3 shadow-[0_0_25px_rgba(30,107,255,0.6)]">
            <p className="text-xs font-black">全国 34施設</p>
            <p className="text-xs font-semibold">2025年5月 日本上陸</p>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="relative z-10 pb-6 md:pb-8 flex flex-col items-center gap-2 animate-bounce">
        <span className="text-white/60 text-xs tracking-widest">SCROLL</span>
        <div className="w-5 h-5 flex items-center justify-center">
          <i className="ri-arrow-down-line text-white/60 text-base" />
        </div>
      </div>
    </section>
  );
}
